import { useState } from 'react'
import type { AdminFeedback } from './adminHelpers'

// Non-component logic (hooks, error mapping, redaction, idempotency keys)
// lives in `adminHelpers.ts`, imported separately by every page — same
// split as B4's `botControlActions.ts` (logic) / `BotActionNotice.tsx`
// (component), which keeps `react-refresh/only-export-components` quiet
// (a re-export barrel here would still trip it on the non-component names).
// This file exports components ONLY.

export function AdminNotice({ feedback }: { feedback: AdminFeedback | undefined }) {
  if (!feedback) return null
  return (
    <p role={feedback.tone === 'error' ? 'alert' : 'status'} className={`portal-notice portal-notice--${feedback.tone}`}>
      {feedback.text}
    </p>
  )
}

/** A small "look up a user" form shared by every email-scoped admin page —
 * an `<input>` + explicit "Load" button (not fetch-on-every-keystroke: an
 * admin page hitting a BUSINESS-CRITICAL backend on every keystroke is its
 * own kind of footgun). */
export function EmailLookupForm({
  email,
  onSubmit,
  label = 'User e-mail',
}: {
  email: string
  onSubmit: (email: string) => void
  label?: string
}) {
  const [draft, setDraft] = useState(email)
  return (
    <form
      className="portal-toolbar"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(draft.trim())
      }}
    >
      <label htmlFor="admin-email-lookup">{label}</label>
      <input
        id="admin-email-lookup"
        type="email"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="user@example.com"
      />
      <button type="submit" disabled={!draft.trim()}>
        Load
      </button>
    </form>
  )
}

/** Inline confirmation for a destructive/irreversible-looking admin action
 * (product delete, payment cancel) — "confirmations inline" per ticket
 * 4b6a6d24, not a modal: a second click surface right next to the button
 * that triggered it, so the admin never loses the row's context. */
export function ConfirmButton({
  label,
  confirmLabel = 'Yes, confirm',
  pendingText,
  disabled,
  onConfirm,
}: {
  label: string
  confirmLabel?: string
  pendingText: string
  disabled?: boolean
  onConfirm: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  if (confirming) {
    return (
      <span className="portal-confirm-inline">
        <span>{pendingText}</span>
        <button
          type="button"
          onClick={() => {
            setConfirming(false)
            onConfirm()
          }}
        >
          {confirmLabel}
        </button>
        <button type="button" onClick={() => setConfirming(false)}>
          Cancel
        </button>
      </span>
    )
  }
  return (
    <button type="button" disabled={disabled} onClick={() => setConfirming(true)}>
      {label}
    </button>
  )
}

/** Section switcher for a page with several independent admin sub-views —
 * reuses the `.portal-tabs`/`.portal-tab` CSS the trading pages already
 * ship (`settingsShared.tsx`), as buttons rather than links since these
 * switch in-page state, not routes. */
export function AdminTabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string }[]
  active: T
  onChange: (id: T) => void
}) {
  return (
    <nav className="portal-tabs" role="tablist" aria-label="Sections">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          className={active === tab.id ? 'portal-tab portal-tab--active' : 'portal-tab'}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  )
}
